import React, { useState, useEffect, useRef } from 'react'
import { Form, Input, Button, Space, Row, Col, Select, Radio, Checkbox } from 'antd'
import { MailOutlined, PhoneOutlined, MessageOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import types from '../../../../Stores/types'

const EULA_PDF_URL = '/fedex-eula.pdf'

const { Option } = Select

const COUNTRY_OPTIONS = [
	{ code: 'US', name: 'United States' },
	{ code: 'CA', name: 'Canada' },
	{ code: 'MX', name: 'Mexico' },
]

const BTN_COLOR = '#479ccf'
const primaryBtnStyle = { backgroundColor: BTN_COLOR, borderColor: BTN_COLOR, color: '#fff' }
const radioStyle = { display: 'block', marginBottom: 10, fontSize: '14px' }

// Country-specific State/Province and ZIP/Postal Code formats
const ADDRESS_FORMAT_RULES = {
	US: {
		state: { pattern: /^[A-Z]{2}$/, message: 'State/Province must be exactly 2 uppercase letters (e.g., CA, NY, TX)' },
		postal_code: { pattern: /^\d{5}(-\d{4})?$/, message: 'ZIP/Postal Code must be in 12345 format' },
	},
	CA: {
		state: { pattern: /^[A-Z]{2}$/, message: 'State/Province must be 2 uppercase letters (e.g., ON, QC, BC)' },
		postal_code: { pattern: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/, message: 'ZIP/Postal Code must be in format A1A1A1' },
	},
	MX: {
		state: { pattern: /^[A-Z]{2}$/, message: 'State/Province must be 2 uppercase letters (e.g., AG, BS, CM)' },
		postal_code: { pattern: /^\d{5}$/, message: 'ZIP/Postal Code must be in format 44100' },
	},
}

// Returns { state?: string, postal_code?: string } error messages for fields that
// don't match the selected country's format. Empty object means address is valid.
function validateAddress(country, address) {
	const rules = ADDRESS_FORMAT_RULES[country]
	const errors = {}
	if (!rules) return errors

	if (!rules.state.pattern.test(address.state ?? '')) {
		errors.state = rules.state.message
	}
	if (!rules.postal_code.pattern.test(address.postal_code ?? '')) {
		errors.postal_code = rules.postal_code.message
	}
	return errors
}

// Step 0: EULA
// Step 1: Nickname + API type + Account Details
// Step 2: Choose Identity Method (Invoice / Security Code)
// Step 3: PIN Delivery Option (security_code path only)
// Step 4: Enter 6-digit code (security_code) OR Invoice details (invoice)
// Step 5: Success

export default function FedexAccountRegistrationForm({ onValidate, onSave, loading, successData, onBack, apiResponse, initialSettings, nicknamePlaceholder = 'e.g., FedEx Production', apiTypeValue, onApiTypeChange, step5Content }) {
	const dispatch = useDispatch()
	const { FedexSmallApiType } = useSelector(state => state)

	const resolvedApiType = apiTypeValue !== undefined ? apiTypeValue : FedexSmallApiType
	const handleApiTypeChange = onApiTypeChange !== undefined
		? onApiTypeChange
		: value => dispatch({ type: types.SET_FEDEX_SMALL_API_TYPE, payload: value })

	const isAlreadyConnected = !!(initialSettings?.child_key && initialSettings?.child_secret)
	const [currentStep, setCurrentStep] = useState(isAlreadyConnected ? 5 : 0)
	const [step1Form] = Form.useForm()
	const [step4Form] = Form.useForm()
	const [step5Form] = Form.useForm()
	const [step1Values, setStep1Values] = useState(isAlreadyConnected ? {
		account_number: initialSettings.account_number ?? '',
		address:        initialSettings.address ?? initialSettings.billing_address ?? '',
		address2:       initialSettings.address2 ?? '',
		city:           initialSettings.city ?? initialSettings.billing_city ?? '',
		state:          initialSettings.state ?? initialSettings.billing_state ?? '',
		postal_code:    initialSettings.postal_code ?? initialSettings.billing_zip ?? '',
		country:        initialSettings.country ?? initialSettings.billing_country ?? '',
	} : {})
	const [mfaMethod, setMfaMethod] = useState('security_code')
	const [pinDelivery, setPinDelivery] = useState('SMS')
	const [accountToken, setAccountToken] = useState(null)
	const [credentials, setCredentials] = useState(
		isAlreadyConnected
			? { child_key: initialSettings.child_key, child_secret: initialSettings.child_secret }
			: { child_key: '', child_secret: '' }
	)
	const [eulaScrolled, setEulaScrolled] = useState(false)
	const [eulaAgreed, setEulaAgreed] = useState(false)
	const [apiCallLoading, setApiCallLoading] = useState(false)
	const [phoneNumber, setPhoneNumber] = useState('')
	const [pinOptions, setPinOptions] = useState(['EMAIL', 'SMS', 'CALL'])
	const [isReconnecting, setIsReconnecting] = useState(false)
	const awaitingStep = useRef(null)

	// Update modal title — only step 4 changes the title
	useEffect(() => {
		let title = 'Connection Settings'
		if (currentStep === 4) {
			title = mfaMethod === 'invoice' ? 'Invoice Validation' : 'Secure Code Validation'
		}
		dispatch({ type: types.SET_CONNECTION_SETTINGS_TITLE, payload: title })
		return () => dispatch({ type: types.SET_CONNECTION_SETTINGS_TITLE, payload: 'Connection Settings' })
	}, [currentStep, mfaMethod, dispatch])

	// Safety net: if isInstalling drops to false but button is still loading, unblock it.
	// Do NOT touch awaitingStep here — the apiResponse useEffect owns that ref.
	// Clearing it here would race with apiResponse useEffect and prevent step advancement.
	useEffect(() => {
		if (!loading && apiCallLoading) {
			setApiCallLoading(false)
		}
	}, [loading]) // eslint-disable-line react-hooks/exhaustive-deps

	// Advance steps on successful API response — any response that lacks the required
	// success marker is treated as an error so the user cannot proceed
	useEffect(() => {
		if (!apiResponse || awaitingStep.current === null) return

		const waiting = awaitingStep.current

		if (!apiResponse.error) {
			// Account connected directly at address validation — skip MFA steps entirely
			if (waiting === 'to2' && apiResponse.registration_success && apiResponse.child_key) {
				const creds = { child_key: apiResponse.child_key ?? '', child_secret: apiResponse.child_secret ?? '' }
				setCredentials(creds)
				onSave({ ...step1Values, ...creds })
				setCurrentStep(5)
				awaitingStep.current = null
				setApiCallLoading(false)
				return
			}
			if (waiting === 'to2' && apiResponse.account_token) {
				setAccountToken(apiResponse.account_token)
				setPhoneNumber(apiResponse.phoneNumber ?? '')
				const preferredOrder = ['EMAIL', 'SMS', 'CALL']
				const secureCodeOptions = apiResponse.options?.secureCode
				const sorted = Array.isArray(secureCodeOptions) && secureCodeOptions.length > 0
					? preferredOrder.filter(o => secureCodeOptions.includes(o))
					: preferredOrder
				setPinOptions(sorted)
				setPinDelivery(sorted.includes('SMS') ? 'SMS' : sorted[0])
				setCurrentStep(2)
				awaitingStep.current = null
				setApiCallLoading(false)
				return
			}
			if (waiting === 'to4' && apiResponse.pin_sent) {
				setCurrentStep(4)
				awaitingStep.current = null
				setApiCallLoading(false)
				return
			}
			if (waiting === 'to5' && apiResponse.registration_success) {
				const creds = { child_key: apiResponse.child_key ?? '', child_secret: apiResponse.child_secret ?? '' }
				setCredentials(creds)
				// Auto-save carrier settings now that registration is complete
				onSave({ ...step1Values, ...creds })
				setCurrentStep(5)
				awaitingStep.current = null
				setApiCallLoading(false)
				return
			}
			// Response came back without error but missing required marker — block the user
		}

		// Error or missing marker: reset loading, stay on current step
		awaitingStep.current = null
		setApiCallLoading(false)
	}, [apiResponse])

	const handleEulaAccept = () => setCurrentStep(1)

	const handleStep1Next = async () => {
		try {
			const values = await step1Form.validateFields()
			setStep1Values(values)
			// Trigger address validation immediately — catches wrong account numbers here
			setApiCallLoading(true)
			awaitingStep.current = 'to2'
			onValidate({ ...values, registration_step: 1, requestForNewConnection: isReconnecting ? 1 : 0 })
		} catch {
			// inline validation shown
		}
	}

	const handleStep2Next = () => {
		if (mfaMethod === 'security_code') {
			setCurrentStep(3)
		} else {
			// Invoice path: token already obtained at step 1, go straight to invoice entry
			setCurrentStep(4)
		}
	}

	const handleStep3Next = () => {
		// Trigger PIN generation with saved token
		setApiCallLoading(true)
		awaitingStep.current = 'to4'
		onValidate({
			...step1Values,
			mfa_method: 'security_code',
			pin_delivery: pinDelivery,
			account_token: accountToken,
			registration_step: 2,
			requestForNewConnection: isReconnecting ? 1 : 0,
		})
	}

	const handleStep4Next = async () => {
		try {
			const values = await step4Form.validateFields()
			setApiCallLoading(true)
			awaitingStep.current = 'to5'
			onValidate({
				...step1Values,
				mfa_method: mfaMethod,
				pin_delivery: mfaMethod === 'security_code' ? pinDelivery : null,
				...values,
				account_token: accountToken,
				registration_step: 3,
				requestForNewConnection: isReconnecting ? 1 : 0,
			})
		} catch {
			// inline validation shown
		}
	}

	const handleReconnect = () => {
		step1Form.resetFields()
		step4Form.resetFields()
		if (initialSettings?.nickname) {
			step1Form.setFieldsValue({ nickname: initialSettings.nickname })
		}
		setStep1Values({})
		setMfaMethod('security_code')
		setPinDelivery('SMS')
		setAccountToken(null)
		setCredentials({ child_key: '', child_secret: '' })
		setEulaScrolled(false)
		setEulaAgreed(false)
		setApiCallLoading(false)
		setIsReconnecting(true)
		awaitingStep.current = null
		setCurrentStep(0)
	}

	const pinOptionIcon  = { SMS: <MessageOutlined style={{ marginRight: 6 }} />, EMAIL: <MailOutlined style={{ marginRight: 6 }} />, CALL: <PhoneOutlined style={{ marginRight: 6 }} /> }
	const pinOptionLabel = { SMS: 'SMS', EMAIL: 'Email', CALL: 'Voice Call' }

	return (
		<div>
			{/* ── Persistent notes ─────────────────────────────────────────── */}
			<div className='note-bx' style={{ marginBottom: 8 }}>
				<strong>Note!</strong> You must have a FedEx account to use this application. If you do not have one, contact FedEx at 800-463-3339 or{' '}
				<a href='http://www.fedex.com/us/oadr/' target='_blank' rel='noreferrer'>register online</a>.
			</div>
			<div className='note-bx' style={{ marginBottom: 14 }}>
				FedEx service marks are owned by Federal Express Corporation and are used by permission.
			</div>

			{/* ── Persistent API type selector (hidden on step 1 and step 5 — step 5 renders it inline after the success banner) */}
			{currentStep !== 1 && currentStep !== 5 && (
				<Form layout='vertical' size='large' style={{ marginBottom: 0 }}>
					<Form.Item label='Which API Will You Connect To?' style={{ marginBottom: 12 }}>
						<Select
							value={resolvedApiType}
							options={[
								{ label: 'Legacy API', value: 'legacy_api' },
								{ label: 'REST API (pre June 1, 2026)', value: 'new_api' },
								{ label: 'REST MFA API (eff. June 1, 2026)', value: 'account_registration_api' },
							]}
							onChange={handleApiTypeChange}
						/>
					</Form.Item>
				</Form>
			)}

			{/* ── Step 0 : EULA ────────────────────────────────────────────── */}
			{currentStep === 0 && (
				<div>
					<div
						style={{
							height: 360,
							border: '1px solid #d9d9d9',
							borderRadius: 4,
							marginBottom: 14,
							overflow: 'auto',
						}}
						onScroll={e => {
							const el = e.currentTarget
							if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
								setEulaScrolled(true)
							}
						}}>
						<iframe
							src={`${EULA_PDF_URL}#toolbar=0&navpanes=0&scrollbar=0`}
							title='FedEx EULA'
							style={{ width: '100%', height: '1600px', border: 'none', display: 'block' }}
						/>
					</div>

					<div style={{ marginBottom: 14 }}>
						<Checkbox
							disabled={!eulaScrolled}
							checked={eulaAgreed}
							onChange={e => setEulaAgreed(e.target.checked)}>
							I have read and agree to the FedEx End User License Agreement. I confirm that I am authorized to accept this agreement on behalf of myself or the legal entity I represent.
						</Checkbox>
					</div>

					<div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
						<Button
							type='primary'
							disabled={!eulaAgreed}
							style={eulaAgreed ? primaryBtnStyle : {}}
							onClick={handleEulaAccept}>
							Next
						</Button>
					</div>
				</div>
			)}

			{/* ── Step 1 : Account Details ─────────────────────────────────── */}
			{currentStep === 1 && (
				<Form
					form={step1Form}
					layout='vertical'
					size='large'
					initialValues={{
						residential_flag: false,
						country: 'US',
						nickname: initialSettings?.nickname ?? '',
					}}>

					<Form.Item label='Which API Will You Connect To?'>
						<Select
							value={resolvedApiType}
							options={[
								{ label: 'Legacy API', value: 'legacy_api' },
								{ label: 'REST API (pre June 1, 2026)', value: 'new_api' },
								{ label: 'REST MFA API (eff. June 1, 2026)', value: 'account_registration_api' },
							]}
							onChange={handleApiTypeChange}
						/>
					</Form.Item>
					
					<Form.Item
						label='Nickname'
						name='nickname'
						required
						rules={[{ required: true, message: 'Nickname is required' }]}>
						<Input placeholder={nicknamePlaceholder} />
					</Form.Item>

					

					<Form.Item
						label='FedEx Account Number'
						name='account_number'
						required
						rules={[
							{ required: true, message: 'FedEx Account Number is required' },
							{ len: 9, message: 'FedEx Account Number must be exactly 9 digits' },
							{ pattern: /^\d{9}$/, message: 'FedEx Account Number must contain only digits' },
						]}>
						<Input placeholder='e.g., 123456789' maxLength={9} />
					</Form.Item>

					<Form.Item
						label='Customer Name'
						name='customer_name'
						required
						rules={[{ required: true, message: 'Customer Name is required' }]}>
						<Input placeholder='e.g., ABC Logistics LLC' />
					</Form.Item>

					<Form.Item label='Residential Address?' name='residential_flag' initialValue={false}>
						<Select>
							<Option value={false}>No</Option>
							<Option value={true}>Yes</Option>
						</Select>
					</Form.Item>

					<Form.Item
						label='Street Line 1'
						name='address'
						required
						rules={[{ required: true, message: 'Street Line 1 is required' }]}>
						<Input placeholder='e.g., 123 Main St' />
					</Form.Item>

					<Form.Item label='Street Line 2' name='address2'>
						<Input placeholder='e.g., Suite 200 (optional)' />
					</Form.Item>

					<Form.Item
						label='City'
						name='city'
						required
						rules={[{ required: true, message: 'City is required' }]}>
						<Input placeholder='e.g., Memphis' />
					</Form.Item>

					<Form.Item
						label='State/Province'
						name='state'
						required
						dependencies={['country']}
						rules={[
							{ required: true, message: 'State/Province is required' },
							({ getFieldValue }) => ({
								validator(_, value) {
									if (!value) return Promise.resolve()
									const errors = validateAddress(getFieldValue('country'), { state: value, postal_code: getFieldValue('postal_code') })
									return errors.state ? Promise.reject(new Error(errors.state)) : Promise.resolve()
								},
							}),
						]}
						extra={<span style={{ fontSize: '11px', color: '#888' }}>Enter the standard two character abbreviation.</span>}>
						<Input placeholder='e.g., TN' />
					</Form.Item>

					<Form.Item
						label='ZIP/Postal Code'
						name='postal_code'
						required
						dependencies={['country']}
						rules={[
							{ required: true, message: 'ZIP/Postal Code is required' },
							({ getFieldValue }) => ({
								validator(_, value) {
									if (!value) return Promise.resolve()
									const errors = validateAddress(getFieldValue('country'), { state: getFieldValue('state'), postal_code: value })
									return errors.postal_code ? Promise.reject(new Error(errors.postal_code)) : Promise.resolve()
								},
							}),
						]}>
						<Input placeholder='e.g., 38116' />
					</Form.Item>

					<Form.Item
						label='Country'
						name='country'
						initialValue='US'
						required
						rules={[{ required: true, message: 'Country is required' }]}>
						<Select showSearch optionFilterProp='children'>
							{COUNTRY_OPTIONS.map(c => (
								<Option key={c.code} value={c.code}>{c.name}</Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
						<Space>
							<Button size='large' onClick={() => setCurrentStep(0)}>Back</Button>
							<Button type='primary' size='large' style={primaryBtnStyle} loading={apiCallLoading} onClick={handleStep1Next}>Next</Button>
						</Space>
					</Form.Item>
				</Form>
			)}

			{/* ── Step 2 : Choose Identity Method ──────────────────────────── */}
			{currentStep === 2 && (
				<div>
					<p style={{ fontWeight: 600, marginBottom: 12 }}>Choose the option to confirm your identity</p>

					<Radio.Group value={mfaMethod} onChange={e => setMfaMethod(e.target.value)} style={{ marginBottom: 20 }}>
						<Radio value='invoice' style={radioStyle}>Invoice Validation</Radio>
						<Radio value='security_code' style={radioStyle}>Secure Code Validation</Radio>
					</Radio.Group>

					<div style={{ textAlign: 'right' }}>
						<Space>
							<Button size='large' onClick={() => setCurrentStep(1)}>Back</Button>
							<Button type='primary' size='large' style={primaryBtnStyle} loading={apiCallLoading} onClick={handleStep2Next}>Next</Button>
						</Space>
					</div>
				</div>
			)}

			{/* ── Step 3 : PIN Delivery Option (security_code only) ────────── */}
			{currentStep === 3 && (
				<div>
					<p style={{ fontWeight: 600, marginBottom: 12 }}>Choose the Secure Code PIN Delivery Option</p>

					<Radio.Group value={pinDelivery} onChange={e => setPinDelivery(e.target.value)} style={{ marginBottom: 20 }}>
						{pinOptions.map(opt => (
							<Radio key={opt} value={opt} style={radioStyle}>
								{pinOptionIcon[opt] ?? null}
								{pinOptionLabel[opt] ?? opt}
								{phoneNumber && opt !== 'EMAIL' && (
									<span style={{ color: '#888', fontSize: '12px', marginLeft: 8 }}>{phoneNumber}</span>
								)}
							</Radio>
						))}
					</Radio.Group>

					<div style={{ textAlign: 'right' }}>
						<Space>
							<Button size='large' onClick={() => setCurrentStep(2)}>Back</Button>
							<Button type='primary' size='large' style={primaryBtnStyle} loading={apiCallLoading} onClick={handleStep3Next}>Next</Button>
						</Space>
					</div>
				</div>
			)}

			{/* ── Step 4 : Enter Code / Invoice Details ────────────────────── */}
			{currentStep === 4 && (
				<Form
					form={step4Form}
					layout='vertical'
					size='large'>
					{mfaMethod === 'security_code' ? (
						<Form.Item
							label='6 Digit Secure Code'
							name='pin'
							rules={[
								{ required: true, message: 'Security code is required' },
								{ len: 6, message: 'Must be exactly 6 digits' },
								{ pattern: /^\d{6}$/, message: 'Digits only' },
							]}>
							<Input placeholder='e.g., 123456' maxLength={6} />
						</Form.Item>
					) : (
						<>
							<Row gutter={12}>
								<Col span={12}>
									<Form.Item label='Invoice Number' name='invoice_number' rules={[{ required: true, message: 'Invoice Number is required' }]}>
										<Input placeholder='e.g., 234562278' />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label='Invoice Date (YYYY-MM-DD)' name='invoice_date' rules={[{ required: true, message: 'Invoice Date is required' }]}>
										<Input placeholder='e.g., 2026-05-12' />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={12}>
								<Col span={12}>
									<Form.Item label='Invoice Amount' name='invoice_amount' rules={[{ required: true, message: 'Invoice Amount is required' }]}>
										<Input placeholder='e.g., 234.00' />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label='Currency' name='invoice_currency' initialValue='USD' rules={[{ required: true, message: 'Currency is required' }]}>
										<Select>
											<Option value='USD'>USD</Option>
											<Option value='CAD'>CAD</Option>
											<Option value='MXN'>MXN</Option>
										</Select>
									</Form.Item>
								</Col>
							</Row>
						</>
					)}

					<Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
						<Space>
							<Button size='large' onClick={() => setCurrentStep(mfaMethod === 'security_code' ? 3 : 2)}>Back</Button>
							<Button type='primary' size='large' style={primaryBtnStyle} loading={apiCallLoading} onClick={handleStep4Next}>Next</Button>
						</Space>
					</Form.Item>
				</Form>
			)}

			{/* ── Step 5 : Success ─────────────────────────────────────────── */}
			{currentStep === 5 && (
				step5Content
					? step5Content({ step1Values, credentials, handleReconnect, onValidate, initialSettings, resolvedApiType, handleApiTypeChange })
					: (
						<div>
							<div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, padding: '16px 20px', marginBottom: 16 }}>
								<div style={{ color: '#389e0d', fontWeight: 700, fontSize: '15px' }}>FedEx Account Connected Successfully</div>
								<div style={{ color: '#52c41a', fontSize: '13px', marginTop: 4 }}>Your FedEx account credentials have been verified and saved.</div>
							</div>

							<Form layout='vertical' size='large' style={{ marginBottom: 0 }}>
								<Form.Item label='Which API Will You Connect To?' style={{ marginBottom: 12 }}>
									<Select
										value={resolvedApiType}
										options={[
											{ label: 'Legacy API', value: 'legacy_api' },
											{ label: 'REST API (pre June 1, 2026)', value: 'new_api' },
											{ label: 'REST MFA API (eff. June 1, 2026)', value: 'account_registration_api' },
										]}
										onChange={handleApiTypeChange}
									/>
								</Form.Item>
							</Form>

							<Form layout='vertical' size='large' form={step5Form}
								initialValues={{ nickname: step1Values?.nickname ?? initialSettings?.nickname ?? '' }}
								onFinish={values => onSave({ ...step1Values, ...credentials, nickname: values.nickname })}>
								<Form.Item label='Nickname' name='nickname' required
									rules={[{ required: true, message: 'Nickname is required' }]}>
									<Input placeholder={nicknamePlaceholder} />
								</Form.Item>
								<Form.Item label='FedEx Account Number'>
									<Input value={step1Values?.account_number ?? ''} disabled />
								</Form.Item>

								<Row gutter={12}>
									<Col span={12}>
										<Form.Item label='Address'>
											<Input value={[step1Values?.address ?? initialSettings?.address, step1Values?.address2 ?? initialSettings?.address2].filter(Boolean).join(', ')} disabled />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item style={{ marginTop: 48 }}>
											<Input value={step1Values?.city ?? initialSettings?.city ?? ''} placeholder='City' disabled />
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={12}>
									<Col span={12}>
										<Form.Item>
											<Input value={step1Values?.state ?? initialSettings?.state ?? ''} placeholder='State' disabled />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item>
											<Input value={step1Values?.postal_code ?? initialSettings?.postal_code ?? ''} placeholder='Zip Code' disabled />
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={12}>
									<Col span={12}>
										<Form.Item>
											<Input value={COUNTRY_OPTIONS.find(c => c.code === (step1Values?.country ?? initialSettings?.country))?.name ?? step1Values?.country ?? initialSettings?.country ?? ''} placeholder='Country' disabled />
										</Form.Item>
									</Col>
								</Row>

								{/* <Form.Item label='Child Key'>
									<Input type='password' value={credentials.child_key} disabled />
								</Form.Item>
								<Form.Item label='Child Secret'>
									<Input type='password' value={credentials.child_secret} disabled />
								</Form.Item> */}
								<Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
									<Space>
										<Button
											type='primary'
											size='large'
											style={primaryBtnStyle}
											onClick={() => onValidate({
												...step1Values,
												...credentials,
												mfa_method: mfaMethod,
												registration_step: 'test',
											})}>
											Test Connection
										</Button>
										<Button type='primary' size='large' style={primaryBtnStyle} htmlType='submit'>
											Save Settings
										</Button>
										<Button type='primary' size='large' style={primaryBtnStyle} onClick={handleReconnect}>
											Reconnect Account
										</Button>
									</Space>
								</Form.Item>
							</Form>
						</div>
					)
			)}

		</div>
	)
}
